import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { listAnalyses, deleteAnalysis } from "@/lib/api";
import EvaluationSheet from "@/components/EvaluationSheet";
import StatusTag from "@/components/StatusTag";
import { useLang } from "@/lib/lang-context";
import { useAuth } from "@/lib/auth-context";
import { Trash2, ChevronRight, Search, Lock } from "lucide-react";
import { toast } from "sonner";

const FILTERS = ["ALL", "BARGAIN", "FAIR", "OVERPRICED"];

const History = () => {
  const { t } = useLang();
  const { user, loading: authLoading } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [focusId, setFocusId] = useState(params.get("focus"));
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listAnalyses({ limit: 100 });
      setItems(data);
      if (!focusId && data.length) setFocusId(data[0].id);
    } finally {
      setLoading(false);
    }
  };

  // Only fetch the archive when the user is authenticated. The archive is
  // private per-user since Iteration 23 — anonymous deep-linkers see the
  // sign-in gate below instead.
  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line
  }, [user]);

  const filtered = useMemo(() => {
    return items.filter((a) => {
      if (filter !== "ALL" && a.status !== filter) return false;
      if (query && !`${a.town || ""} ${a.title || ""}`.toLowerCase().includes(query.toLowerCase()))
        return false;
      return true;
    });
  }, [items, filter, query]);

  const focused = useMemo(() => items.find((a) => a.id === focusId), [items, focusId]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("history.confirm"))) return;
    try {
      await deleteAnalysis(id);
      toast.success("Deleted");
      const next = items.filter((a) => a.id !== id);
      setItems(next);
      if (focusId === id) setFocusId(next[0]?.id ?? null);
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Auth gate — placed AFTER all hooks so we don't break the rules-of-hooks
  // ordering. Anonymous callers see a friendly sign-in CTA instead of the
  // archive shell + an inevitable empty list.
  if (!authLoading && !user) {
    return (
      <main
        data-testid="history-gate"
        className="max-w-2xl mx-auto px-6 md:px-10 py-20 text-center"
      >
        <div className="inline-flex items-center justify-center bg-[#F2EFE6] border border-[#E6E4DD] p-3 mb-6">
          <Lock size={20} strokeWidth={1.5} className="text-[#4A5D23]" />
        </div>
        <div className="label-eyebrow">Archive</div>
        <h1 className="font-serif-display text-4xl md:text-5xl mt-3 text-[#282C2A]">
          {t("history.title")}
        </h1>
        <p className="mt-4 text-[#6C706D]">
          {t("history.gateMessage")}
        </p>
        <Link
          to="/login"
          data-testid="history-gate-cta"
          className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 text-xs font-semibold uppercase tracking-wider bg-[#282C2A] text-white hover:bg-[#4A5D23] transition-colors"
        >
          {t("quota.registerCta")}
        </Link>
      </main>
    );
  }

  return (
    <main data-testid="history-page" className="max-w-7xl mx-auto px-6 md:px-10 py-12">
      <header className="mb-10">
        <div className="label-eyebrow">Archive</div>
        <h1 className="font-serif-display text-5xl md:text-6xl mt-3 text-[#282C2A]">
          {t("history.title")}
        </h1>
        <p className="mt-3 text-[#6C706D] max-w-xl">{t("history.sub")}</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C706D]" />
              <input
                data-testid="history-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Lecce, Ostuni, Maglie…"
                className="w-full pl-9 pr-3 py-2.5 border border-[#E6E4DD] focus:outline-none focus:border-[#4A5D23] bg-white text-sm font-mono"
              />
            </div>
          </div>
          <div className="flex gap-1 mb-5 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => {
                  setFilter(f);
                  setParams({});
                }}
                className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider border transition-colors ${
                  filter === f
                    ? "bg-[#282C2A] text-white border-[#282C2A]"
                    : "bg-white border-[#E6E4DD] text-[#6C706D] hover:border-[#282C2A]"
                }`}
              >
                {f === "ALL" ? t("history.all") : t(`status.${f}`)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-sm text-[#6C706D] font-mono">…</div>
          ) : filtered.length === 0 ? (
            <p data-testid="history-empty" className="text-sm text-[#6C706D]">{t("history.empty")}</p>
          ) : (
            <ul className="divide-y divide-[#E6E4DD] border-y border-[#E6E4DD]">
              {filtered.map((a) => (
                <li key={a.id}>
                  <button
                    onClick={() => setFocusId(a.id)}
                    data-testid={`history-item-${a.id}`}
                    className={`w-full text-left py-4 px-2 flex items-center gap-4 transition-colors ${
                      focusId === a.id ? "bg-[#F0EFEB]" : "hover:bg-[#F7F6F2]"
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="label-eyebrow">{a.town || "—"}</div>
                      <div className="font-serif-display text-lg text-[#282C2A] truncate mt-1">
                        {a.title || a.area_description || "Untitled"}
                      </div>
                      <div className="font-mono text-xs text-[#6C706D] mt-1">
                        € {a.price_per_sqm ? a.price_per_sqm.toLocaleString("it-IT") : "—"} / m² · {new Date(a.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <StatusTag status={a.status} />
                      <span className="font-serif-display text-2xl text-[#282C2A]">{a.total_score}</span>
                      <ChevronRight size={16} className="text-[#6C706D]" />
                    </div>
                  </button>
                  <div className="px-2 pb-3">
                    <button
                      onClick={() => handleDelete(a.id)}
                      data-testid={`delete-${a.id}`}
                      className="text-xs text-[#A0522D] hover:underline inline-flex items-center gap-1"
                    >
                      <Trash2 size={12} /> {t("history.delete")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="lg:col-span-7">
          {focused ? <EvaluationSheet a={focused} /> : null}
        </section>
      </div>
    </main>
  );
};

export default History;
